// Адрес раздела «Рынок»: выбранная вкладка и селекторы каждой вкладки. Ссылка,
// которой делятся, открывает тот же разрез. Источник правды — сам адрес, а не
// состояние компонента: вкладка, открытая заново, читает выбор оттуда же.
//
// Ключи с приставкой `p`: общие `lat`/`date`/`species` держит вид карты, и
// смешивать их нельзя. Значение по умолчанию в адрес не пишется — ссылка без
// выбора остаётся короткой, а умолчание вкладки решает вкладка.

import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

export const PRICES_TABS = ['export', 'wholesale', 'wholesale_ru', 'retail', 'analytics', 'summary'] as const
export type PricesTab = (typeof PRICES_TABS)[number]

export const PRICES_URL_KEYS = {
  tab: 'pt',
  exportSpecies: 'pe',
  wholesaleSpecies: 'pw',
  ruWholesaleSpecies: 'pu',
  analyticsSpecies: 'pa',
  retailSource: 'pr',
  retailTerritory: 'pc',
  chainsCity: 'pk',
  chainsDay: 'pd',
} as const
export type PricesUrlKey = keyof typeof PRICES_URL_KEYS

export function parseTab(v: string | null): PricesTab {
  return (PRICES_TABS as readonly string[]).includes(v ?? '') ? (v as PricesTab) : 'export'
}

/** Новый адрес из прежнего: `null` и пустая строка снимают ключ. Чужие ключи
 *  остаются — адрес общий с видом карты. */
export function patchPricesParams(
  prev: URLSearchParams, patch: Partial<Record<PricesUrlKey, string | null>>,
): URLSearchParams {
  const next = new URLSearchParams(prev)
  for (const [k, v] of Object.entries(patch) as [PricesUrlKey, string | null][]) {
    const key = PRICES_URL_KEYS[k]
    if (v == null || v === '') next.delete(key)
    else next.set(key, v)
  }
  return next
}

/** Чтение и запись выбора «Рынка». Несколько ключей пишутся ОДНИМ вызовом:
 *  два `setSearchParams` подряд читают один и тот же прежний адрес, и второй
 *  затёр бы первый. */
export function usePricesUrl() {
  const [params, setParams] = useSearchParams()
  const get = useCallback((k: PricesUrlKey) => params.get(PRICES_URL_KEYS[k]), [params])
  const set = useCallback(
    (patch: Partial<Record<PricesUrlKey, string | null>>) =>
      setParams((prev) => patchPricesParams(prev, patch), { replace: true }),
    [setParams],
  )
  return { get, set }
}
